'use client';

import dynamic from 'next/dynamic';
import { useApartmentReviewsContext } from './ApartmentReviewsProvider';
import { ReviewsShell, type ReviewSourceConfig } from './ReviewsShell';
import { ReviewsEntryPoint } from './ReviewsEntryPoint';

const GoogleReviewsPanel = dynamic(() => import('./GoogleReviewsPanel'));
const ReputationMockPanel = dynamic(() => import('./ReputationMockPanel'));
const GoogleMockPanel = dynamic(() => import('./ProviderMockPanels').then((module) => module.GoogleMockPanel));
const RedditMockPanel = dynamic(() => import('./ProviderMockPanels').then((module) => module.RedditMockPanel));

export function ApartmentReviewsModule() {
  const { lotId, reviews, mockEnabled, integrationPreview, googlePlaceId } = useApartmentReviewsContext();
  if (mockEnabled) {
    return (
      <div>
        <p role="note">
          {integrationPreview
            ? 'Preview · synthetic Reputation and Reddit data'
            : 'Preview · synthetic Figma data for all three providers'}
        </p>
        <ReviewsShell
          key={lotId}
          id="reviews"
          sources={[
            { id: 'web', label: 'Web reviews', panel: { status: 'ready', render: () => <ReputationMockPanel /> } },
            {
              id: 'google',
              label: 'Google',
              eager: integrationPreview,
              instanceKey: googlePlaceId,
              panel: {
                status: 'ready',
                render: () =>
                  integrationPreview && googlePlaceId ? (
                    <GoogleReviewsPanel placeId={googlePlaceId} preview />
                  ) : (
                    <GoogleMockPanel />
                  ),
              },
            },
            { id: 'reddit', label: 'Reddit', panel: { status: 'ready', render: () => <RedditMockPanel /> } },
          ]}
        />
      </div>
    );
  }
  if (!reviews && !googlePlaceId) return null;
  const sources: Array<ReviewSourceConfig> = [];
  if (reviews?.reputation) sources.push({ id: 'web', label: 'Web reviews', panel: { status: 'unavailable' } });
  if (googlePlaceId)
    sources.push({
      id: 'google',
      label: 'Google',
      instanceKey: googlePlaceId,
      eager: true,
      panel: {
        status: 'ready',
        render: () => <GoogleReviewsPanel placeId={googlePlaceId} preview={integrationPreview} />,
      },
    });
  if (reviews?.reddit) sources.push({ id: 'reddit', label: 'Reddit', panel: { status: 'unavailable' } });
  return <ReviewsShell key={lotId} id="reviews" sources={sources} />;
}

export function ApartmentReviewsEntry() {
  const { reviews, mockEnabled, googlePlaceId } = useApartmentReviewsContext();
  if (mockEnabled) {
    return (
      <ReviewsEntryPoint
        targetId="reviews"
        hasReputation
        aggregateRating={{ rating: 4.3, reviewCount: 231 }}
        hasGooglePlace
        hasRedditLinks
      />
    );
  }
  if (!reviews && !googlePlaceId) return null;
  const { averageRating, totalReviews } = reviews?.reputation ?? {};
  return (
    <ReviewsEntryPoint
      targetId="reviews"
      hasReputation={!!reviews?.reputation}
      aggregateRating={
        averageRating !== undefined && totalReviews !== undefined
          ? { rating: averageRating, reviewCount: totalReviews }
          : null
      }
      hasGooglePlace={!!googlePlaceId}
      hasRedditLinks={!!reviews?.reddit?.links.length}
    />
  );
}
